import { IconCheck, IconEraser, IconLoader2 } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import ToolCard from '../../components/common/ToolCard';

/**
 * What the scan found in the uploaded PDF, and which of it to take out.
 *
 * Everything found is ticked to begin with, because a file usually carries one
 * watermark and the common case should need no decisions. The list matters when
 * it carries more than one — a draft stamp plus a company logo, say — or when
 * the scan has picked up something that is not a watermark at all, which is
 * exactly when being able to untick it saves the document.
 *
 * Each row says WHY it was flagged (faint, turned, on every page), so the
 * judgement is the reader's rather than a black box's.
 */
export default function WatermarkRemovePanel({
    scanning, detection, selected, onToggle, locked, lockLabel, LockWrapper,
}) {
    const { t } = useTranslation();
    const digits = (n) => toBnDigits(n);
    const candidates = detection?.candidates || [];

    if (scanning) {
        return (
            <ToolCard title={t('watermarkPdf.remove.title')}>
                <p className="flex items-center gap-2 text-[13px] text-zinc-500 dark:text-zinc-400">
                    <IconLoader2 size={15} className="animate-spin" />
                    {t('watermarkPdf.remove.scanning')}
                </p>
            </ToolCard>
        );
    }

    if (!candidates.length) {
        return (
            <ToolCard title={t('watermarkPdf.remove.title')}>
                <p className="text-[13px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                    {t('watermarkPdf.remove.none')}
                </p>
            </ToolCard>
        );
    }

    const reasonFor = (mark) => {
        const reasons = [];
        if (mark.alpha != null && mark.alpha < 0.92) {
            reasons.push(t('watermarkPdf.remove.reasonFaint', { value: digits(Math.round(mark.alpha * 100)) }));
        }
        if (mark.rotated) reasons.push(t('watermarkPdf.remove.reasonRotated'));
        reasons.push(t('watermarkPdf.remove.reasonPages', { count: digits(mark.pages) }));
        return reasons.join(' · ');
    };

    const body = (
        <div className="space-y-2">
            {candidates.map((mark) => {
                const on = selected.includes(mark.id);
                return (
                    <button
                        key={mark.id}
                        type="button"
                        onClick={() => onToggle(mark.id)}
                        disabled={locked}
                        aria-pressed={on}
                        className={`flex w-full items-start gap-2.5 rounded-xl border p-2.5 text-left transition-colors disabled:cursor-not-allowed ${
                            on
                                ? 'border-primary bg-primary/5'
                                : 'border-zinc-200 hover:border-primary/40 dark:border-zinc-700'
                        }`}
                    >
                        <span className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded border ${
                            on ? 'border-primary bg-primary text-white' : 'border-zinc-300 dark:border-zinc-600'
                        }`}>
                            {on ? <IconCheck size={11} stroke={3} /> : null}
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                                <span className="truncate text-[13px] font-semibold text-zinc-800 dark:text-zinc-100">
                                    {mark.kind === 'text' ? `“${mark.label}”` : t(`watermarkPdf.remove.kind_${mark.kind}`)}
                                </span>
                            </span>
                            <span className="mt-0.5 block text-[11.5px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                                {reasonFor(mark)}
                            </span>
                        </span>
                    </button>
                );
            })}
            <p className="flex items-start gap-1.5 pt-1 text-[11.5px] leading-relaxed text-zinc-400 dark:text-zinc-500">
                <IconEraser size={13} className="mt-0.5 flex-none" />
                {t('watermarkPdf.remove.hint')}
            </p>
        </div>
    );

    return (
        <ToolCard title={t('watermarkPdf.remove.title')}>
            {LockWrapper && locked ? <LockWrapper label={lockLabel}>{body}</LockWrapper> : body}
        </ToolCard>
    );
}
